import difficultyData from './difficulty.json';

export interface TechniqueInfo {
  id: string;
  /** Oyun seviyesi (1-5), difficulty.json'dan türetilir. Etiket: i18n difficulties[level]. */
  level: DifficultyTier;
}

/**
 * TEK KAYNAK zorluk eşlemesi (bkz. difficulty.json).
 * Solver'ın emit ettiği GERÇEK teknik ismi → oyun seviyesi (1-5).
 * Backend rate_difficulty ile aynı JSON'u paylaşır → bir daha diverge etmez.
 */
export type DifficultyTier = 1 | 2 | 3 | 4 | 5;

export const TECHNIQUE_LEVELS: Record<string, DifficultyTier> =
  (difficultyData as { levels: Record<string, number> }).levels as Record<string, DifficultyTier>;

/**
 * Solver adımının teknik ismini oyun seviyesine (1-5) çevirir.
 * Bilinmeyen isimde 5 döner + uyarı loglar (yeni teknik eklenince yakalamak için).
 * Seviye ismini göstermek için: i18n `difficulties[String(techniqueLevel(name))]`.
 */
export function techniqueLevel(technique: string): DifficultyTier {
  const lvl = TECHNIQUE_LEVELS[technique];
  if (lvl === undefined) {
    if (typeof console !== 'undefined') {
      console.warn(`[difficulty] Bilinmeyen teknik ismi: "${technique}" → 5 varsayıldı`);
    }
    return 5;
  }
  return lvl;
}

/**
 * Eğitim katalogu slug'ı → solver'ın GERÇEK teknik ismi (difficulty.json anahtarı).
 * Seviye buradan türetilir → oyun/solver ile TEK ÖLÇEK, ayrı bir zorluk tablosu yok.
 * (grouped-x-cycles solver'da ayrı emit edilmez; X-Cycles ile aynı seviye = 5.)
 */
const SLUG_TO_TECHNIQUE: Record<string, string> = {
  'naked-single': 'Naked Single',
  'hidden-single': 'Hidden Single',
  'naked-pair': 'Naked Pair',
  'naked-triple': 'Naked Triple',
  'naked-quad': 'Naked Quad',
  'hidden-pair': 'Hidden Pair',
  'hidden-triple': 'Hidden Triple',
  'hidden-quad': 'Hidden Quad',
  'pointing': 'Pointing Pairs',
  'box-line-reduction': 'Box/Line Reduction',
  'x-wing': 'X-Wing',
  'swordfish': 'Swordfish',
  'jellyfish': 'Jellyfish',
  'xy-wing': 'XY-Wing',
  'xyz-wing': 'XYZ-Wing',
  'wxyz-wing': 'WXYZ-Wing',
  'simple-coloring': 'Simple Colouring',
  'remote-pairs': 'Remote Pairs',
  'w-wing': 'W-Wing',
  'skyscraper': 'Skyscraper',
  'two-string-kite': '2-String Kite',
  'empty-rectangle': 'Empty Rectangle',
  'unique-rectangle': 'Unique Rectangle',
  'hidden-unique-rectangle': 'Hidden Unique Rectangle',
  'extended-unique-rectangle': 'Extended Unique Rectangle',
  'rectangle-elimination': 'Rectangle Elimination',
  'bug-plus-1': 'BUG+1',
  'finned-x-wing': 'Finned X-Wing',
  'finned-swordfish': 'Finned Swordfish',
  'x-cycles': 'X-Cycles',
  'grouped-x-cycles': 'X-Cycles',
  '3d-medusa': '3D Medusa',
  'xy-chain': 'XY-Chain',
  'aic': 'AIC',
  'sue-de-coq': 'Sue-de-Coq',
  'aligned-pair-exclusion': 'Aligned Pair Exclusion',
  'almost-locked-sets': 'Almost Locked Sets',
  'death-blossom': 'Death Blossom',
  'digit-forcing-chains': 'Digit Forcing Chains',
  'cell-forcing-chains': 'Cell Forcing Chains',
  'unit-forcing-chains': 'Unit Forcing Chains',
  'nishio-forcing-chains': 'Nishio Forcing Chains',
  'pattern-overlay': 'Pattern Overlay',
  'exocet': 'Exocet',
  'bowmans-bingo': "Bowman's Bingo",
};

/**
 * Solver'ın GERÇEK teknik ismi → eğitim slug'ı (ters eşleme). "X-Cycles" hem
 * x-cycles hem grouped-x-cycles'a karşılık gelir (solver ikisi için de aynı
 * ismi emit ediyor) — ilk eşleşme (x-cycles) kazanır.
 */
export const TECHNIQUE_NAME_TO_SLUG: Record<string, string> = {};
for (const [slug, name] of Object.entries(SLUG_TO_TECHNIQUE)) {
  if (!(name in TECHNIQUE_NAME_TO_SLUG)) TECHNIQUE_NAME_TO_SLUG[name] = slug;
}

/** Solver adımının teknik ismini eğitim katalogu slug'ına çevirir (yoksa undefined). */
export function techniqueSlug(technique: string): string | undefined {
  return TECHNIQUE_NAME_TO_SLUG[technique];
}

/**
 * Eğitim katalogu — ÖĞRETİM SIRASINA göre (tekli → altküme → kesişim → balık →
 * kanat → renklendirme/desen → teklik → zincir → uzman). Seviye tek kaynaktan.
 */
export const TECHNIQUES: TechniqueInfo[] = Object.keys(SLUG_TO_TECHNIQUE).map((id) => ({
  id,
  level: techniqueLevel(SLUG_TO_TECHNIQUE[id]),
}));
